/**
 * RunningTasksChip — "N running tasks" beside the environment selector.
 *
 * Reads the durable ``subagent_runs`` ledger (ADR-0019), not the transient
 * ``subagents`` progress map. That choice is the whole point of the widget: the
 * live map is fed by ``subagent_progress`` frames and cleared at every turn
 * boundary, so it goes blank the instant a turn ends — which is exactly when a
 * *delegated* run is still going, since it deliberately outlives its turn. The
 * ledger is also what the Sub-agents panel reads, so the count on the chip and
 * the "Active · N" heading behind it can never disagree.
 *
 * Renders nothing when nothing is running: a permanent zero would be one more
 * chip to scan past on a row that is already crowded (model, environment,
 * tokens, cost, files) and a horizontal scroller on a narrow screen.
 */

import { useChatStore } from "@/stores/chatStore";

export function RunningTasksChip() {
  const running = useChatStore(
    (s) => s.subagentRuns.filter((r) => r.status === "running").length,
  );
  const openRightPanel = useChatStore((s) => s.openRightPanel);

  if (running === 0) return null;

  const label = `${running} running task${running === 1 ? "" : "s"}`;

  return (
    <button
      type="button"
      onClick={() => openRightPanel("subagents")}
      title="Background work still in flight — open the Sub-agents panel"
      aria-label={`${label}. Open the Sub-agents panel.`}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium
                 whitespace-nowrap transition-colors
                 bg-jove-30 text-jove ring-1 ring-jove/25 hover:bg-jove-50"
    >
      {/* Same pulsing dot the sidebar uses for a running chat — one visual
          vocabulary for "the machine is busy", wherever it's surfaced. */}
      <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-jove-60 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-jove" />
      </span>
      <span>{label}</span>
    </button>
  );
}
